import { Art } from '@/modules/marketing/SiteArt';
import type { ArtProps } from '@/modules/marketing/SiteArt';

/**
 * The illustrations for the monitoring articles, by the names the pages import.
 *
 * Pictures drawn by `scripts/site-art.py` and shown by `SiteArt`. Each article has one at the top
 * that shows what the monitor is, and most have a second, smaller one beside the paragraph it
 * explains: the anatomy of a check, a run of results, a countdown, a lookup, a handshake. The two
 * are different pictures, so a page does not show the same drawing twice.
 */

/** A monitor requesting a page from outside, and the three things it checks. */
export function WebsiteMonitoringArt({ priority }: ArtProps = {}) {
  return <Art name="website-monitoring" priority={priority} />;
}

/** A request to a health endpoint and the response it is held to. */
export function ApiMonitoringArt({ priority }: ArtProps = {}) {
  return <Art name="api-monitoring" priority={priority} />;
}

/** A server's ports, one of them refusing. */
export function PortMonitoringArt({ priority }: ArtProps = {}) {
  return <Art name="port-monitoring" priority={priority} />;
}

/** A domain's expiry date, read from the registry. */
export function DomainExpiryArt({ priority }: ArtProps = {}) {
  return <Art name="domain-expiry" priority={priority} />;
}

/** A request going out and a response coming back, inspected rather than trusted. */
export function ResponseArt({ priority }: ArtProps = {}) {
  return <Art name="response-checks" priority={priority} />;
}

/** A run of checks with an outage in the middle, and the incident it produced. */
export function UptimeArt({ priority }: ArtProps = {}) {
  return <Art name="incident-record" priority={priority} />;
}

/** A certificate with a countdown, warning before the browser warns anybody. */
export function CertCountdownArt({ priority }: ArtProps = {}) {
  return <Art name="cert-countdown" priority={priority} />;
}

/** A name resolved, and the answer compared against what it should be. */
export function LookupArt({ priority }: ArtProps = {}) {
  return <Art name="dns-lookup" priority={priority} />;
}

/** A connection accepted, and one refused. */
export function PortArt({ priority }: ArtProps = {}) {
  return <Art name="port-states" priority={priority} />;
}

/** A registration running out, with nothing on the server able to see it. */
export function RegistrationArt({ priority }: ArtProps = {}) {
  return <Art name="registration" priority={priority} />;
}
